import { Button, ToggleButton, ToggleButtonGroup, Box, Typography } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import React, { useState, useRef, useEffect, useCallback } from 'react';

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

const SignatureCanvas = ({ getValues, setValue, trigger }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [mode, setMode] = useState('draw');
    const [uploadPreview, setUploadPreview] = useState(null);
    const [uploadError, setUploadError] = useState('');
    const [signatureSaved, setSignatureSaved] = useState(false);

    // --- Responsive canvas sizing ---
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const containerWidth = container.offsetWidth;
        const width = Math.min(containerWidth, 500);
        const height = Math.round(width / 2);

        // Preserve existing drawing when resizing
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, width, height);
    }, []);

    useEffect(() => {
        if (mode === 'draw') {
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);
            return () => window.removeEventListener('resize', resizeCanvas);
        }
    }, [mode, resizeCanvas]);

    // --- Get coordinates from mouse or touch event ---
    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.touches && e.touches.length > 0) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        return {
            x: e.nativeEvent.offsetX,
            y: e.nativeEvent.offsetY
        };
    };

    // --- Draw mode handlers ---
    const startDrawing = (e) => {
        if (e.touches) e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getCoordinates(e);
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.moveTo(x, y);
        setDrawing(true);
        setSignatureSaved(false);
    };

    const endDrawing = (e) => {
        if (e.touches) e.preventDefault();
        setDrawing(false);
    };

    const draw = (e) => {
        if (!drawing) return;
        if (e.touches) e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getCoordinates(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setValue("signatureImage", "");
        setSignatureSaved(false);
    };

    const saveSignature = () => {
        const canvas = canvasRef.current;
        const signatureData = canvas.toDataURL('image/png');
        setValue("signatureImage", signatureData);
        setSignatureSaved(true);
    };

    // --- Upload mode handlers ---
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadError('');

        if (!ALLOWED_TYPES.includes(file.type)) {
            setUploadError('Only PNG and JPEG images are allowed.');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setUploadError('File size must be under 8MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            setUploadPreview(dataUrl);
            setValue("signatureImage", dataUrl);
        };
        reader.readAsDataURL(file);
    };

    const clearUpload = () => {
        setUploadPreview(null);
        setUploadError('');
        setValue("signatureImage", "");
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleModeChange = (e, newMode) => {
        if (newMode !== null) {
            setMode(newMode);
        }
    };

    return (
        <div ref={containerRef}>
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                size="small"
                sx={{ mb: 1.5 }}
            >
                <ToggleButton value="draw">
                    <DrawIcon sx={{ mr: 0.5, fontSize: 18 }} /> Draw
                </ToggleButton>
                <ToggleButton value="upload">
                    <UploadFileIcon sx={{ mr: 0.5, fontSize: 18 }} /> Upload
                </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'draw' ? (
                <div>
                    <canvas
                        style={{
                            border: signatureSaved ? "2px solid #4caf50" : "1px solid #000",
                            display: 'block',
                            touchAction: 'none',
                            maxWidth: '100%'
                        }}
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseUp={endDrawing}
                        onMouseMove={draw}
                        onMouseLeave={endDrawing}
                        onTouchStart={startDrawing}
                        onTouchEnd={endDrawing}
                        onTouchMove={draw}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button type="button" variant="contained" size="small" onClick={clearCanvas}>Reset</Button>
                        <Button
                            type="button"
                            variant="contained"
                            size="small"
                            onClick={saveSignature}
                            color={signatureSaved ? "success" : "primary"}
                            startIcon={signatureSaved ? <CheckCircleIcon /> : null}
                        >
                            {signatureSaved ? "Signature Confirmed" : "Confirm Signature"}
                        </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {signatureSaved
                            ? "Signature confirmed. Click Update below to save your changes."
                            : "Draw your signature above, then click Confirm Signature."}
                    </Typography>
                </div>
            ) : (
                <div>
                    <Box
                        sx={{
                            border: '2px dashed',
                            borderColor: uploadError ? 'error.main' : 'divider',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploadPreview ? (
                            <img src={uploadPreview} alt="Signature preview" style={{ maxHeight: 200, maxWidth: '100%' }} />
                        ) : (
                            <>
                                <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Click to upload signature image (PNG or JPEG, max 8MB)
                                </Typography>
                            </>
                        )}
                    </Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    {uploadError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {uploadError}
                        </Typography>
                    )}
                    {uploadPreview && (
                        <Box sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" size="small" onClick={clearUpload}>Remove</Button>
                        </Box>
                    )}
                </div>
            )}
        </div>
    );
};

export default SignatureCanvas;
